import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Smartphone, Eye, Play, Pause } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "../components/ui/dialog";

/**
 * Mobile app showcase — 3 phone-frame mockups built around real iPhone
 * screenshots of the Cleany cleaning-staff app. Designed to make the
 * "mobil-først" promise tangible since 80 % of cleaners interact via mobile.
 *
 * The first phone has a "Se demo" overlay button that opens a Dialog with an
 * auto-cycling loop of all three screens.
 */
const screens = [
    {
        title: "Dagens opgaver i lommen",
        desc: "Når Anders åbner appen, ser han med det samme dagens kunder, adresser og status. Et tryk og han er i gang.",
        bullet: "Alle dagens kunder, adresser og status — på 2 sek.",
        accent: "from-sky-500 to-sky-700",
        src: "/mobile/overblik.png",
        alt: "Skærmbillede af dagens opgavelisten i Cleany mobilappen",
    },
    {
        title: "Klar arbejdsbeskrivelse",
        desc: "Hver opgave har en konkret beskrivelse + tjekliste. Ingen flere SMS-spørgsmål til kontoret om hvad der skal laves.",
        bullet: "Konkret beskrivelse + tjekliste pr. opgave — ingen tvivl.",
        accent: "from-emerald-500 to-emerald-700",
        src: "/mobile/task_detail.png",
        alt: "Skærmbillede af opgave-detalje med beskrivelse og tjekliste",
    },
    {
        title: "Køreopgave med GPS",
        desc: "Tryk start — appen logger kørsel automatisk og viser ruten på kortet. Lønbilag og kørselsregnskab ordner sig selv.",
        bullet: "Auto-logget kørsel med GPS — kørselsregnskab på autopilot.",
        accent: "from-amber-500 to-amber-600",
        src: "/mobile/korsel_map.png",
        alt: "Skærmbillede af køreopgave med kort og GPS-tracking",
    },
];

export default function MobileShowcase() {
    const [demoOpen, setDemoOpen] = useState(false);

    return (
        <section
            className="py-24 md:py-32 bg-gradient-to-b from-slate-900 via-slate-950 to-slate-900 text-white relative overflow-hidden"
            data-testid="mobile-showcase-section"
        >
            <div className="absolute inset-0 bg-grid opacity-[0.06]" />
            <div className="absolute -top-40 left-1/4 h-96 w-96 rounded-full bg-sky-500/10 blur-3xl" />
            <div className="absolute -bottom-40 right-1/4 h-96 w-96 rounded-full bg-violet-500/10 blur-3xl" />

            <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
                <div className="text-center max-w-3xl mx-auto">
                    <div className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/20 px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-sky-300">
                        <Smartphone className="h-3 w-3" />
                        Mobil-først
                    </div>
                    <h2 className="mt-5 font-display text-3xl md:text-5xl font-bold tracking-tight">
                        Bygget til hverdagen — også på vej til kunden.
                    </h2>
                    <p className="mt-4 text-base md:text-lg text-slate-300 leading-relaxed">
                        Medarbejdere bruger Cleany på telefonen 80&nbsp;% af tiden. Hver funktion er
                        optimeret til hurtig brug med tommeltot — på 5G eller offline.
                    </p>
                </div>

                <div className="mt-16 grid md:grid-cols-3 gap-12 md:gap-6">
                    {screens.map((s, i) => (
                        <motion.div
                            key={s.title}
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6, delay: i * 0.12 }}
                            className="flex flex-col items-center text-center"
                            data-testid={`mobile-screen-${i}`}
                        >
                            <PhoneFrame>
                                <img
                                    src={s.src}
                                    alt={s.alt}
                                    loading="lazy"
                                    className="absolute inset-0 w-full h-full object-cover object-top"
                                />
                                {i === 0 && (
                                    <SeeDemoOverlay onClick={() => setDemoOpen(true)} />
                                )}
                            </PhoneFrame>
                            <h3 className={`mt-8 font-display text-xl font-bold bg-gradient-to-r ${s.accent} bg-clip-text text-transparent`}>
                                {s.title}
                            </h3>
                            <p className="mt-2 text-sm text-slate-400 leading-relaxed max-w-xs">
                                {s.desc}
                            </p>
                        </motion.div>
                    ))}
                </div>

                <div className="mt-16 text-center">
                    <p className="text-xs text-slate-400">
                        <span className="font-semibold text-slate-300">Bemærk:</span> Skærmbillederne
                        er fra en intern test-konto. Live-app&apos;en lanceres 1.&nbsp;oktober&nbsp;2026.
                    </p>
                </div>
            </div>

            <DemoDialog open={demoOpen} onOpenChange={setDemoOpen} />
        </section>
    );
}

/* ---------- Phone frame ---------- */
function PhoneFrame({ children }) {
    return (
        <div className="relative">
            <div className="absolute -inset-4 bg-gradient-to-tr from-sky-500/15 via-cyan-400/10 to-transparent rounded-[3rem] blur-2xl" />
            <div className="relative w-[260px] h-[540px] bg-slate-950 rounded-[2.5rem] border-[10px] border-slate-800 shadow-2xl shadow-sky-900/40 overflow-hidden">
                {/* Notch */}
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-6 bg-slate-950 rounded-b-2xl z-20" />
                {/* Screen */}
                <div className="absolute inset-0 bg-white overflow-hidden">{children}</div>
            </div>
        </div>
    );
}

/* ---------- "Se demo" overlay button on the first phone ---------- */
function SeeDemoOverlay({ onClick }) {
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid="mobile-see-demo-btn"
            aria-label="Se demo af mobilappen"
            className="group absolute inset-0 z-10 flex items-end justify-center pb-6
                       bg-gradient-to-t from-black/60 via-black/10 to-transparent
                       opacity-90 hover:opacity-100 transition-opacity"
        >
            <span
                className="inline-flex items-center gap-2 rounded-full
                           bg-white text-slate-900
                           px-4 py-2 text-xs font-bold tracking-wide
                           shadow-xl shadow-sky-900/40
                           transition-transform duration-300
                           group-hover:scale-105 group-hover:-translate-y-0.5"
            >
                <Eye className="h-3.5 w-3.5" strokeWidth={2.5} />
                Se demo
            </span>
        </button>
    );
}

/* ---------- Auto-cycling demo dialog ---------- */
function DemoDialog({ open, onOpenChange }) {
    const [index, setIndex] = useState(0);
    const [playing, setPlaying] = useState(true);

    // Reset to first slide whenever the dialog (re-)opens.
    useEffect(() => {
        if (open) {
            setIndex(0);
            setPlaying(true);
        }
    }, [open]);

    // Auto-cycle every 2.8 s while playing and dialog open.
    useEffect(() => {
        if (!open || !playing) return undefined;
        const id = setInterval(
            () => setIndex((i) => (i + 1) % screens.length),
            2800,
        );
        return () => clearInterval(id);
    }, [open, playing]);

    const current = screens[index];

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="bg-slate-950/95 border-slate-800 text-white max-w-md
                           p-6 sm:p-8 rounded-3xl"
                data-testid="mobile-demo-dialog"
            >
                <DialogTitle className="text-center font-display text-lg font-bold">
                    Mobil-app demo
                </DialogTitle>
                <p className="text-center text-xs text-slate-400 -mt-2 mb-2">
                    3 skærme i sekvens — auto-loop
                </p>

                <div className="flex justify-center pt-2">
                    <PhoneFrame>
                        <AnimatePresence mode="wait">
                            <motion.img
                                key={current.src}
                                src={current.src}
                                alt={current.alt}
                                initial={{ opacity: 0, scale: 1.02 }}
                                animate={{ opacity: 1, scale: 1 }}
                                exit={{ opacity: 0, scale: 0.99 }}
                                transition={{ duration: 0.45 }}
                                className="absolute inset-0 w-full h-full object-cover object-top"
                            />
                        </AnimatePresence>
                    </PhoneFrame>
                </div>

                <div className="mt-5 text-center min-h-[64px]">
                    <div
                        className={`inline-block font-display text-base font-bold
                                    bg-gradient-to-r ${current.accent} bg-clip-text text-transparent`}
                        data-testid="mobile-demo-title"
                    >
                        {current.title}
                    </div>
                    <AnimatePresence mode="wait">
                        <motion.p
                            key={current.bullet}
                            initial={{ opacity: 0, y: 4 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -4 }}
                            transition={{ duration: 0.35 }}
                            className="mt-1.5 text-xs text-slate-300 leading-relaxed
                                       max-w-[260px] mx-auto"
                            data-testid="mobile-demo-bullet"
                        >
                            {current.bullet}
                        </motion.p>
                    </AnimatePresence>
                </div>

                {/* progress dots + play/pause */}
                <div className="mt-5 flex items-center justify-center gap-3">
                    <div className="flex items-center gap-1.5">
                        {screens.map((s, i) => (
                            <button
                                key={s.src}
                                type="button"
                                onClick={() => setIndex(i)}
                                aria-label={`Gå til skærm ${i + 1}`}
                                data-testid={`mobile-demo-dot-${i}`}
                                className={`h-1.5 rounded-full transition-all
                                            ${i === index
                                                ? "bg-white w-6"
                                                : "bg-white/30 w-1.5 hover:bg-white/60"}`}
                            />
                        ))}
                    </div>
                    <button
                        type="button"
                        onClick={() => setPlaying((p) => !p)}
                        aria-label={playing ? "Pause demo" : "Afspil demo"}
                        data-testid="mobile-demo-play-toggle"
                        className="ml-1 inline-flex items-center justify-center
                                   h-6 w-6 rounded-full bg-white/10 border border-white/20
                                   hover:bg-white/20 transition-colors"
                    >
                        {playing
                            ? <Pause className="h-3 w-3" />
                            : <Play className="h-3 w-3" />}
                    </button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
